import React from "react";
import { useFormikContext } from "formik";
import ReviewHeader from "../common/ReviewHeader";
import useSalesFormStore from "../../../../../../../../stores/sales-form";
import CheckIcon from "../../../../../../../../assets/svg/icons/common/CheckIcon";
import CloseIcon from "../../../../../../../../assets/svg/icons/common/CloseIcon";
import { premiseTypeOptions } from "../../../../../../../../../constants";
import baseStyles from "../../style.module.scss";
import styles from "./style.module.scss";

const BusinessInformation = ({ onEdit, isOpen, onToggle, complete }) => {
  const { values } = useFormikContext();
  const mccOptions = useSalesFormStore((state) => state.mccOptions);
  const applicationData = useSalesFormStore((state) => state.applicationData);
  const hasPaylianceProcessor =
    applicationData?.override_bank?.some(
      (bank) => bank.label === "Payliance",
    ) ?? false;

  const formatPeriod = (years, months) => {
    const yearText = years > 0 ? `${years} year${years > 1 ? "s" : ""}` : "";
    const monthText = months > 0 ? `${months} mon` : "";
    return [yearText, monthText].filter(Boolean).join(" ");
  };

  const getBusinessTypeName = (id) => {
    if (!id || !mccOptions) return "-";
    const option = mccOptions.find((opt) => opt.value === id);
    if(option){
      return option.label;
    }else if(applicationData?.mcc_info){
      return `${applicationData.mcc_info.description} (${applicationData.mcc_info.mcc})`;
    }else {
      return "-";
    }
  };

  const SectionHeader = ({ title }) => (
    <div className={baseStyles.sectionHeader}>
      <span className={baseStyles.sectionTitle}>{title}</span>
      <div className={baseStyles.sectionLine} />
    </div>
  );

  return (
    <div
      className={`${baseStyles.reviewSectionContainer} ${styles.business_information}`}
    >
      <ReviewHeader
        title="Business Information"
        isOpen={isOpen}
        onToggle={onToggle}
        onEdit={onEdit}
        complete={complete}
      />

      {isOpen && (
        <div className={baseStyles.reviewContent}>
          <div className={baseStyles.detailsGrid}>
            <div className={baseStyles.detailItem}>
              <div className={baseStyles.detailLabel}>Type of Ownership</div>
              <div className={baseStyles.detailValue}>
                {values.type_of_ownership || "-"}
              </div>
            </div>
            <div className={baseStyles.detailItem}>
              <div className={baseStyles.detailLabel}>Legal Business Name</div>
              <div className={baseStyles.detailValue}>
                {values.legal_business_name || "-"}
              </div>
            </div>
            <div className={baseStyles.detailItem}>
              <div className={baseStyles.detailLabel}>DBA Name</div>
              <div className={baseStyles.detailValue}>
                {values.dba_name || "-"}
              </div>
            </div>
            <div className={baseStyles.detailItem}>
              <div className={baseStyles.detailLabel}>Tax ID / EIN</div>
              <div className={baseStyles.detailValue}>
                {/* 
                  Remove below formatting for CA apps
                */}
                {values?.tax_ein && values?.tax_ein !== ""
                  ? values.tax_ein.replace(/^(\d{2})(\d+)$/, "$1-$2")
                  : "-"}
              </div>
            </div>
            <div className={baseStyles.detailItem}>
              <div className={baseStyles.detailLabel}>Premise Type</div>
              <div className={baseStyles.detailValue}>
                {premiseTypeOptions.find(
                  (opt) => opt.value === values.premise_type,
                )?.label || "-"}
              </div>
            </div>
            <div className={baseStyles.detailItem}>
              <div className={baseStyles.detailLabel}>Period of Ownership</div>
              <div className={baseStyles.detailValue}>
                {formatPeriod(
                  values.ownership_years,
                  values.ownership_months,
                ) || "-"}
              </div>
            </div>
            <div className={baseStyles.detailItem}>
              <div className={baseStyles.detailLabel}>Business Type</div>
              <div className={baseStyles.detailValue}>
                {getBusinessTypeName(values.business_type)}
              </div>
            </div>
            {hasPaylianceProcessor && (
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>Payliance Industry</div>
                <div className={baseStyles.detailValue}>
                  {values.payliance_industry || "-"}
                </div>
              </div>
            )}
            <div className={baseStyles.detailItem}>
              <div className={baseStyles.detailLabel}>Tax exempt</div>
              <div className={baseStyles.detailValue}>
                <div className={baseStyles.taxExemptContainer}>
                  {values.tax_exempt === "yes" ? (
                    <CheckIcon className={baseStyles.checkIcon} />
                  ) : (
                    <CloseIcon className={baseStyles.closeIcon} />
                  )}
                  <span>{values.tax_exempt === "yes" ? "Yes" : "No"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* <div className={baseStyles.section}>
            <SectionHeader title="BUSINESS ADDRESS" />
            <div className={baseStyles.detailsGrid}>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>Street Address</div>
                <div className={baseStyles.detailValue}>{values.business_address || '-'}</div>
              </div>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>City</div>
                <div className={baseStyles.detailValue}>{values.business_city || '-'}</div>
              </div>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>State</div>
                <div className={baseStyles.detailValue}>{values.business_state || '-'}</div>
              </div>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>Zip</div>
                <div className={baseStyles.detailValue}>{values.business_zip || '-'}</div>
              </div>
            </div>
          </div> */}

          <div className={baseStyles.section}>
            <SectionHeader title="CONTACTS" />
            <div className={baseStyles.detailsGrid}>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>
                  Business Phone Number
                </div>
                <div className={baseStyles.detailValue}>
                  {values.business_phone || "-"}
                </div>
              </div>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>Business Email</div>
                <div className={baseStyles.detailValue}>
                  {values.business_email || "-"}
                </div>
              </div>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>
                  Business Website URL
                </div>
                <div className={baseStyles.detailValue}>
                  {values.business_website || "-"}
                </div>
              </div>
            </div>
          </div>

          <div className={baseStyles.section}>
            <SectionHeader title="DBA Address" />
            <div className={baseStyles.detailsGrid}>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>DBA Street</div>
                <div className={baseStyles.detailValue}>
                  {values.dba_address || "-"}
                </div>
              </div>
            </div>
            <div className={baseStyles.detailsGrid}>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>DBA City</div>
                <div className={baseStyles.detailValue}>
                  {values.dba_city || "-"}
                </div>
              </div>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>DBA State</div>
                <div className={baseStyles.detailValue}>
                  {values.dba_state || "-"}
                </div>
              </div>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>DBA Zip</div>
                <div className={baseStyles.detailValue}>
                  {values.dba_zip || "-"}
                </div>
              </div>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>Period of Location</div>
                <div className={baseStyles.detailValue}>
                  {formatPeriod(
                    values.location_years,
                    values.location_months,
                  ) || "-"}
                </div>
              </div>
            </div>
          </div>

          <div className={baseStyles.section}>
            <SectionHeader title="Legal business Address" />
            <div className={baseStyles.detailsGrid}>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>Legal Street</div>
                <div className={baseStyles.detailValue}>
                  {values.legal_address || "-"}
                </div>
              </div>
            </div>
            <div className={baseStyles.detailsGrid}>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>Legal City</div>
                <div className={baseStyles.detailValue}>
                  {values.legal_city || "-"}
                </div>
              </div>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>Legal State</div>
                <div className={baseStyles.detailValue}>
                  {values.legal_state || "-"}
                </div>
              </div>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>Legal Zip</div>
                <div className={baseStyles.detailValue}>
                  {values.legal_zip || "-"}
                </div>
              </div>
              <div className={baseStyles.detailItem}>
                <div className={baseStyles.detailLabel}>Legal Phone Number</div>
                <div className={baseStyles.detailValue}>
                  {values.legal_phone || "-"}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessInformation;
